import React, { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, List, ListItemIcon, ListItemText, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import { DragHandle, ArrowUpward, ArrowDownward, Delete, Add } from '@mui/icons-material';
import { useFetchUsers } from '../../../../../data/generic-queries/users';

const getUsersName = (user) => user?.name || user?.full_name || '-';
const getUserById = (userOptions, id) => (userOptions || []).find((u) => u._id === id);

const toEntries = (value) => (Array.isArray(value) ? value.filter((e) => e && e.hauler_id) : []);

const parseMaxLoadsInput = (raw) => {
    if (raw === '' || raw === null || raw === undefined) return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) return null;
    return n;
};

const GenericPriorityList = ({ name, props }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [entries, setEntries] = useState([]);
    const { setValue } = useFormContext();
    const priorityList = useWatch({ name });
    const { role, roleLabel = 'User' } = props;

    const { data: options, isLoading } = useFetchUsers({
        where: { role, subuser: { eq: false }, status: { neq: 'Inactive' } },
    });

    const selectedIds = new Set(entries.map((e) => e.hauler_id));
    const availableOptions = (options || []).filter((u) => !selectedIds.has(u._id));

    const openDialog = () => {
        setEntries(toEntries(priorityList));
        setDialogOpen(true);
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;
        const next = Array.from(entries);
        const [moved] = next.splice(result.source.index, 1);
        next.splice(result.destination.index, 0, moved);
        setEntries(next);
    };

    const moveItem = (index, dir) => {
        const newIndex = index + dir;
        if (newIndex < 0 || newIndex >= entries.length) return;
        const next = Array.from(entries);
        const [moved] = next.splice(index, 1);
        next.splice(newIndex, 0, moved);
        setEntries(next);
    };

    const handleAddHauler = (user) => {
        if (!user || selectedIds.has(user._id)) return;
        setEntries([...entries, { hauler_id: user._id, max_loads_per_day: null }]);
    };

    const handleRemoveHauler = (index) => {
        const next = Array.from(entries);
        next.splice(index, 1);
        setEntries(next);
    };

    const handleMaxLoadsChange = (index, raw) => {
        const next = Array.from(entries);
        next[index] = { ...next[index], max_loads_per_day: parseMaxLoadsInput(raw) };
        setEntries(next);
    };

    const handleCancel = () => {
        setEntries([]);
        setDialogOpen(false);
    };

    const handleSubmit = () => {
        setValue(name, entries);
        setDialogOpen(false);
    };

    if (isLoading) return <CircularProgress />;

    const summaryChips = (() => {
        const list = toEntries(priorityList);
        return list
            .map((e, idx) => {
                const u = getUserById(options, e.hauler_id);
                if (!u) return null;
                return { id: e.hauler_id, name: getUsersName(u), max: e.max_loads_per_day, rank: idx + 1 };
            })
            .filter(Boolean);
    })();

    return (
        <Box paddingX={1}>
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
                <DialogTitle sx={{ pt: 3, pb: 1, px: 4, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                        Manage {roleLabel} Priorities
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Drag to reorder (1 = highest priority). Leave Max blank for no cap; enter 0 to exclude.
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ px: 4, pt: 1, pb: 2 }}>
                    <Autocomplete
                        options={availableOptions}
                        value={null}
                        blurOnSelect
                        clearOnBlur
                        size="small"
                        onChange={(_, user) => handleAddHauler(user)}
                        getOptionLabel={(option) => getUsersName(option)}
                        isOptionEqualToValue={(option, value) => option._id === value._id}
                        renderInput={(params) => {
                            const mergedParams = {
                                ...params,
                                inputProps: { ...params.inputProps, 'aria-label': `Add ${roleLabel.toLowerCase()}` },
                                InputProps: {
                                    ...params.InputProps,
                                    startAdornment: (
                                        <>
                                            <Add fontSize="small" sx={{ ml: 0.5, mr: 0.5, color: 'text.secondary' }} />
                                            {params.InputProps.startAdornment}
                                        </>
                                    ),
                                },
                            };
                            return <TextField {...mergedParams} hiddenLabel placeholder={`Add ${roleLabel.toLowerCase()}…`} margin="dense" />;
                        }}
                    />

                    <Typography variant="overline" sx={{ display: 'block', mt: 2, mb: 1, color: 'text.secondary', letterSpacing: 0.6 }}>
                        Priority order
                    </Typography>

                    {entries.length === 0 ? (
                        <Box
                            sx={{
                                py: 5,
                                textAlign: 'center',
                                border: '1px dashed',
                                borderColor: 'divider',
                                borderRadius: 2,
                                color: 'text.secondary',
                            }}
                        >
                            <Typography variant="body2">No {roleLabel.toLowerCase()}s yet — use the search above to add one.</Typography>
                        </Box>
                    ) : (
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="selected-users">
                                {(droppable) => (
                                    <List ref={droppable.innerRef} {...droppable.droppableProps} sx={{ maxHeight: 420, overflow: 'auto', py: 0 }}>
                                        {entries.map((entry, index) => {
                                            const user = getUserById(options, entry.hauler_id);
                                            const label = user ? getUsersName(user) : `User ${entry.hauler_id}`;
                                            const isExcluded = entry.max_loads_per_day === 0;
                                            const inputValue = entry.max_loads_per_day === null || entry.max_loads_per_day === undefined ? '' : String(entry.max_loads_per_day);
                                            return (
                                                <Draggable key={entry.hauler_id} draggableId={entry.hauler_id} index={index}>
                                                    {(draggable, snapshot) => (
                                                        <Box
                                                            ref={draggable.innerRef}
                                                            {...draggable.draggableProps}
                                                            {...draggable.dragHandleProps}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                borderRadius: 2,
                                                                border: '1px solid',
                                                                borderColor: snapshot.isDragging ? 'primary.main' : 'divider',
                                                                px: 2,
                                                                py: 1.25,
                                                                mb: 1,
                                                                bgcolor: snapshot.isDragging ? 'action.hover' : 'background.paper',
                                                                boxShadow: snapshot.isDragging ? 4 : 'none',
                                                                transition: 'background-color 120ms, box-shadow 120ms, border-color 120ms',
                                                                minHeight: 56,
                                                                gap: 1.5,
                                                                '&:hover': { bgcolor: 'action.hover' },
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: 28,
                                                                    height: 28,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    borderRadius: '50%',
                                                                    bgcolor: 'primary.main',
                                                                    color: 'primary.contrastText',
                                                                    fontSize: 13,
                                                                    fontWeight: 600,
                                                                    flexShrink: 0,
                                                                }}
                                                            >
                                                                {index + 1}
                                                            </Box>

                                                            <ListItemIcon sx={{ minWidth: 28, color: 'text.disabled' }}>
                                                                <DragHandle fontSize="small" />
                                                            </ListItemIcon>

                                                            <ListItemText
                                                                primary={
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <Typography sx={{ fontWeight: 500 }}>{label}</Typography>
                                                                        {isExcluded && <Chip label="Excluded" size="small" color="warning" sx={{ height: 22 }} />}
                                                                    </Box>
                                                                }
                                                                sx={{ flex: 1, my: 0 }}
                                                            />

                                                            <Tooltip title="Max loads per day. Empty = no cap. 0 = excluded from this plant.">
                                                                <TextField
                                                                    hiddenLabel
                                                                    size="small"
                                                                    type="number"
                                                                    variant="outlined"
                                                                    value={inputValue}
                                                                    inputProps={{ 'aria-label': 'Max loads per day', min: 0, step: 1, style: { width: 56, textAlign: 'center', padding: '6px 8px' } }}
                                                                    onChange={(e) => handleMaxLoadsChange(index, e.target.value)}
                                                                    placeholder="∞"
                                                                />
                                                            </Tooltip>

                                                            <IconButton size="small" disabled={index === 0} onClick={() => moveItem(index, -1)}>
                                                                <ArrowUpward fontSize="inherit" />
                                                            </IconButton>
                                                            <IconButton size="small" disabled={index === entries.length - 1} onClick={() => moveItem(index, 1)}>
                                                                <ArrowDownward fontSize="inherit" />
                                                            </IconButton>
                                                            <IconButton size="small" aria-label={`Remove ${label}`} onClick={() => handleRemoveHauler(index)}>
                                                                <Delete fontSize="inherit" />
                                                            </IconButton>
                                                        </Box>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {droppable.placeholder}
                                    </List>
                                )}
                            </Droppable>
                        </DragDropContext>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 4, pb: 3, pt: 1, gap: 1 }}>
                    <Button variant="text" color="inherit" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button variant="contained" disableElevation onClick={handleSubmit} sx={{ minWidth: 120 }}>
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {summaryChips.length === 0 ? (
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                            No {roleLabel.toLowerCase()}s selected
                        </Typography>
                    ) : (
                        <Table size="small" sx={{ '& td, & th': { borderBottom: '1px solid', borderColor: 'divider', py: 0.5, px: 1 } }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: 40, color: 'text.secondary', fontSize: 11, fontWeight: 600, letterSpacing: 0.5 }}>#</TableCell>
                                    <TableCell sx={{ color: 'text.secondary', fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>{roleLabel}</TableCell>
                                    <TableCell align="right" sx={{ width: 120, color: 'text.secondary', fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                                        Max loads/day
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {summaryChips.map((c) => {
                                    const isExcluded = c.max === 0;
                                    const isCapped = typeof c.max === 'number' && c.max > 0;
                                    return (
                                        <TableRow key={c.id} hover>
                                            <TableCell sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{c.rank}</TableCell>
                                            <TableCell sx={{ fontWeight: 500 }}>{c.name}</TableCell>
                                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                                {isExcluded ? (
                                                    <Chip label="Excluded" size="small" color="warning" sx={{ height: 20, fontSize: 11 }} />
                                                ) : isCapped ? (
                                                    c.max
                                                ) : (
                                                    <Box component="span" sx={{ color: 'text.disabled' }}>
                                                        ∞
                                                    </Box>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </Box>
                <Button variant="outlined" onClick={openDialog} sx={{ minWidth: 180, flexShrink: 0 }}>
                    Manage Priorities
                </Button>
            </Box>
        </Box>
    );
};

export default GenericPriorityList;
